import React, { useMemo } from 'react';
import { ResumePreviewProps, WorkExperience, Education, Certification, Project } from '../types';
import { usePreviewSettings } from '../templateSettings';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string, yearOnly?: boolean): string => {
  if (!dateString) return '';
  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";
  if (yearOnly) return date.getFullYear().toString();
  if (!dateFormat) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  }
  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);
  switch (dateFormat) {
    case '%b %y': return `${monthShort} ${yearShort}`;
    case '%B %Y': return `${monthLong} ${year}`;
    case '%b %Y': return `${monthShort} ${year}`;
    case '%m/%Y': return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y': return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y': return year.toString();
    default: return `${monthShort} '${yearShort}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string, yearOnly?: boolean, hideDate?: boolean): string => {
  if (hideDate) return '';
  if (!startDate) return '';
  const start = formatDateWithFormat(startDate, dateFormat, yearOnly);
  if (isCurrent) return `${start} - Present`;
  if (!endDate) return start;
  const end = formatDateWithFormat(endDate, dateFormat, yearOnly);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

export const MappleTemplate: React.FC<ResumePreviewProps> = ({ data, activeField, onFieldClick, sectionOrder, structures = [], settings }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    categorySkills,
    links,
    customSections
  } = data;

  const dateFormat = settings?.date_format;
  const { rootStyle } = usePreviewSettings(settings);

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
    'TYPE_LINK_BLOCKS': 'links',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) map[sectionId] = s.structure_name;
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  return (
    <div className="resume-preview resume-mapple" style={rootStyle}>
      <div className="resume-mapple__header">
        <h1 className="resume-mapple__name">
          <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.firstName || ''}
          </EditableField>
          {' '}
          <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick}>
            {personalInfo.lastName || ''}
          </EditableField>
        </h1>
        {targetJobTitle && (
          <p className="resume-mapple__job-title">
            <EditableField fieldName="position" activeField={activeField} onFieldClick={onFieldClick}>
              {targetJobTitle}
            </EditableField>
          </p>
        )}
      </div>

      <div className="resume-mapple__content">
        <div className="resume-mapple__sidebar">
          <div className="resume-mapple__sidebar-section">
            <h3 className="resume-mapple__sidebar-title">Contact</h3>
            <div className="resume-mapple__contact-info">
              {personalInfo.email && (
                <p>
                  <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
                    {personalInfo.email}
                  </EditableField>
                </p>
              )}
              {personalInfo.phone && (
                <p>
                  <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                    {personalInfo.phone}
                  </EditableField>
                </p>
              )}
              {personalInfo.location && (
                <p>
                  <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                    {personalInfo.location}
                  </EditableField>
                </p>
              )}
              {links?.find(l => l.type === 'linkedin')?.url && (
                <p>
                  <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                    {links.find(l => l.type === 'linkedin')?.url}
                  </EditableField>
                </p>
              )}
            </div>
          </div>

          {links && links.length > 0 && (
            <div className="resume-mapple__sidebar-section">
              <h3 className="resume-mapple__sidebar-title">Links</h3>
              {links.map((link, idx) => (
                <EditableBlock key={idx} fieldName={`links.${idx}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__link-item">
                  <p className="resume-mapple__link-type">
                    <EditableField fieldName={`links.${idx}.type`} activeField={activeField} onFieldClick={onFieldClick}>
                      {link.type}
                    </EditableField>
                  </p>
                  <p className="resume-mapple__link-url">
                    <EditableField fieldName={`links.${idx}.url`} activeField={activeField} onFieldClick={onFieldClick}>
                      {link.url}
                    </EditableField>
                  </p>
                </EditableBlock>
              ))}
            </div>
          )}

          {categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0) && (
            <div className="resume-mapple__sidebar-section">
              <h3 className="resume-mapple__sidebar-title">Skills</h3>
              <div className="resume-mapple__skills">
                {Object.entries(categorySkills).map(([category, skills]) => (
                  skills && skills.length > 0 && (
                    <p key={category} className="resume-mapple__skill-line">
                      <EditableField fieldName={`skills.category.${category}`} activeField={activeField} onFieldClick={onFieldClick}>
                        {category !== 'General skills' && <strong>{category}: </strong>}
                        {skills.join(' · ')}
                      </EditableField>
                    </p>
                  )
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="resume-mapple__main">
          {professionalSummary && (
            <div className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{getSectionTitle('about', 'Professional Summary')}</h2>
              <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick}>
                <ul className="resume-mapple__list">
                  {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                    <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
                  ))}
                </ul>
              </EditableBlock>
            </div>
          )}

          {workExperiences && workExperiences.length > 0 && (
            <div className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{getSectionTitle('experience', 'Experience')}</h2>
              {workExperiences.map((exp: WorkExperience) => (
                <EditableBlock key={exp.uid} fieldName={`experience.${exp.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry">
                  <div className="resume-mapple__entry-header">
                    <div className="resume-mapple__entry-left">
                      <div className="resume-mapple__entry-role">
                        <EditableField fieldName={`experience.${exp.uid}.job_title`} activeField={activeField} onFieldClick={onFieldClick}>
                          {exp.job_title}
                        </EditableField>
                      </div>
                      <div className="resume-mapple__entry-company">
                        <EditableField fieldName={`experience.${exp.uid}.employer_name`} activeField={activeField} onFieldClick={onFieldClick}>
                          {exp.employer_name}
                        </EditableField>
                      </div>
                    </div>
                    <div className="resume-mapple__entry-date">
                      <EditableField fieldName={`experience.${exp.uid}.dates`} activeField={activeField} onFieldClick={onFieldClick}>
                        {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                      </EditableField>
                    </div>
                  </div>
                  {exp.description && (
                    <p className="resume-mapple__entry-description">
                      <EditableField fieldName={`experience.${exp.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                        {exp.description}
                      </EditableField>
                    </p>
                  )}
                  {exp.achievements && exp.achievements.length > 0 && (
                    <ul className="resume-mapple__list">
                      {exp.achievements.map((achievement, idx) => (
                        <li key={idx}>
                          <EditableField fieldName={`experience.${exp.uid}.achievements.${idx}`} activeField={activeField} onFieldClick={onFieldClick}>
                            {achievement}
                          </EditableField>
                        </li>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          )}

          {volunteerExperiences && volunteerExperiences.length > 0 && (
            <div className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{getSectionTitle('volunteer', 'Volunteer Experience')}</h2>
              {volunteerExperiences.map((exp: WorkExperience) => (
                <EditableBlock key={exp.uid} fieldName={`volunteer.${exp.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry">
                  <div className="resume-mapple__entry-header">
                    <div className="resume-mapple__entry-left">
                      <div className="resume-mapple__entry-role">
                        <EditableField fieldName={`volunteer.${exp.uid}.job_title`} activeField={activeField} onFieldClick={onFieldClick}>
                          {exp.job_title}
                        </EditableField>
                      </div>
                      <div className="resume-mapple__entry-company">
                        <EditableField fieldName={`volunteer.${exp.uid}.employer_name`} activeField={activeField} onFieldClick={onFieldClick}>
                          {exp.employer_name}
                        </EditableField>
                      </div>
                    </div>
                    <div className="resume-mapple__entry-date">
                      <EditableField fieldName={`volunteer.${exp.uid}.dates`} activeField={activeField} onFieldClick={onFieldClick}>
                        {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                      </EditableField>
                    </div>
                  </div>
                  {exp.achievements && exp.achievements.length > 0 && (
                    <ul className="resume-mapple__list">
                      {exp.achievements.map((achievement, idx) => (
                        <li key={idx}>
                          <EditableField fieldName={`volunteer.${exp.uid}.achievements.${idx}`} activeField={activeField} onFieldClick={onFieldClick}>
                            {achievement}
                          </EditableField>
                        </li>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              ))}
            </div>
          )}

          {educations && educations.length > 0 && (
            <div className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{getSectionTitle('education', 'Education')}</h2>
              {educations.map((edu: Education) => (
                <EditableBlock key={edu.uid} fieldName={`education.${edu.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry resume-mapple__entry--compact">
                  <p>
                    <EditableField fieldName={`education.${edu.uid}.institution`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry-role">
                      {edu.institution}
                    </EditableField>
                    {edu.degree && (
                      <>
                        <span> | </span>
                        <EditableField fieldName={`education.${edu.uid}.degree`} activeField={activeField} onFieldClick={onFieldClick}>
                          {edu.degree}
                        </EditableField>
                      </>
                    )}
                  </p>
                  {edu.field_of_study && (
                    <p>
                      <EditableField fieldName={`education.${edu.uid}.field_of_study`} activeField={activeField} onFieldClick={onFieldClick}>
                        {edu.field_of_study}
                      </EditableField>
                    </p>
                  )}
                  {edu.graduation_date && (
                    <p>
                      Graduation:{' '}
                      <EditableField fieldName={`education.${edu.uid}.graduation_date`} activeField={activeField} onFieldClick={onFieldClick}>
                        {formatDateWithFormat(edu.graduation_date, dateFormat)}
                      </EditableField>
                    </p>
                  )}
                  {edu.grade && (
                    <p>
                      <EditableField fieldName={`education.${edu.uid}.grade`} activeField={activeField} onFieldClick={onFieldClick}>
                        GPA: {edu.grade}
                      </EditableField>
                    </p>
                  )}
                  {edu.description && (
                    <p>
                      <EditableField fieldName={`education.${edu.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                        {edu.description}
                      </EditableField>
                    </p>
                  )}
                </EditableBlock>
              ))}
            </div>
          )}

          {certifications && certifications.length > 0 && (
            <div className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{getSectionTitle('certifications', 'Certifications')}</h2>
              {certifications.map((cert: Certification) => (
                <EditableBlock key={cert.uid} fieldName={`certifications.${cert.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry resume-mapple__entry--compact">
                  <p>
                    <EditableField fieldName={`certifications.${cert.uid}.certification_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry-role">
                      {cert.certification_name}
                    </EditableField>
                    {cert.issued_at && (
                      <>
                        <span> | </span>
                        <EditableField fieldName={`certifications.${cert.uid}.issued_at`} activeField={activeField} onFieldClick={onFieldClick}>
                          {formatDateWithFormat(cert.issued_at, dateFormat)}
                        </EditableField>
                      </>
                    )}
                    {cert.organization_name && (
                      <>
                        <span> | </span>
                        <EditableField fieldName={`certifications.${cert.uid}.organization_name`} activeField={activeField} onFieldClick={onFieldClick}>
                          {cert.organization_name}
                        </EditableField>
                      </>
                    )}
                    {cert.expires_at && (
                      <>
                        <span> | Expires: </span>
                        <EditableField fieldName={`certifications.${cert.uid}.expires_at`} activeField={activeField} onFieldClick={onFieldClick}>
                          {formatDateWithFormat(cert.expires_at, dateFormat)}
                        </EditableField>
                      </>
                    )}
                    {cert.credential_id && (
                      <>
                        <span> | ID: </span>
                        <EditableField fieldName={`certifications.${cert.uid}.credential_id`} activeField={activeField} onFieldClick={onFieldClick}>
                          {cert.credential_id}
                        </EditableField>
                      </>
                    )}
                  </p>
                  {cert.description && (
                    <p>
                      <EditableField fieldName={`certifications.${cert.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                        {cert.description}
                      </EditableField>
                    </p>
                  )}
                </EditableBlock>
              ))}
            </div>
          )}

          {projects && projects.length > 0 && (
            <div className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{getSectionTitle('projects', 'Projects')}</h2>
              {projects.map((project: Project) => {
                const dateRange = project.start_date
                  ? (project.is_current
                      ? `${formatDateWithFormat(project.start_date, dateFormat)} - Present`
                      : project.end_date
                        ? `${formatDateWithFormat(project.start_date, dateFormat)} - ${formatDateWithFormat(project.end_date, dateFormat)}`
                        : formatDateWithFormat(project.start_date, dateFormat))
                  : '';
                return (
                  <EditableBlock key={project.uid} fieldName={`projects.${project.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry">
                    <div className="resume-mapple__entry-header">
                      <div className="resume-mapple__entry-left">
                        <EditableField fieldName={`projects.${project.uid}.project_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry-role">
                          {project.project_name}
                        </EditableField>
                        {project.url && (
                          <>
                            <span> | </span>
                            <EditableField fieldName={`projects.${project.uid}.url`} activeField={activeField} onFieldClick={onFieldClick} className="resume-mapple__entry-company">
                              {project.url}
                            </EditableField>
                          </>
                        )}
                      </div>
                      {dateRange && (
                        <span className="resume-mapple__entry-date">{dateRange}</span>
                      )}
                    </div>
                    {project.description && (
                      <p className="resume-mapple__entry-description">
                        <EditableField fieldName={`projects.${project.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                          {project.description}
                        </EditableField>
                      </p>
                    )}
                    {project.achievements && project.achievements.length > 0 && (
                      <ul className="resume-mapple__list">
                        {project.achievements.map((achievement, idx) => (
                          <li key={idx}>{achievement}</li>
                        ))}
                      </ul>
                    )}
                  </EditableBlock>
                );
              })}
            </div>
          )}

          {customSections?.map((cs) => (
            <div key={cs.id} className="resume-mapple__section">
              <h2 className="resume-mapple__section-title">{cs.title}</h2>
              {cs.layout === 'stacked' ? (
                cs.entries.map((entry) => (
                  <div key={entry.id} className="resume-mapple__entry">
                    <div className="resume-mapple__entry-header">
                      <strong className="resume-mapple__entry-role">{entry.title}</strong>
                      {entry.date && !entry.hideDate && (
                        <span className="resume-mapple__entry-date">
                          {entry.yearOnly
                            ? new Date(entry.date).getFullYear()
                            : formatDateWithFormat(entry.date, dateFormat)}
                          {entry.endDate && !entry.hideEndDate && (
                            <> - {entry.yearOnly
                              ? new Date(entry.endDate).getFullYear()
                              : formatDateWithFormat(entry.endDate, dateFormat)}</>
                          )}
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <p className="resume-mapple__entry-description">{entry.description}</p>
                    )}
                  </div>
                ))
              ) : (
                <ul className="resume-mapple__list">
                  {cs.entries.map((entry) => (
                    <li key={entry.id}>
                      <strong>{entry.title}</strong>
                      {entry.description && `: ${entry.description}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
